import type { Permission } from "@/shared/types/permission.types";

import type { OrganizationEntity } from "./organization.entity";

export const UserType = {
  INTERNAL: "internal",
  ORGANIZATION_MEMBER: "organization_member",
} as const;

export type UserType = (typeof UserType)[keyof typeof UserType];

/**
 * Mirror of the backend `UserRole` enum (`coloca-nest` `src/coloca/users/entities/user.entity.ts`).
 *
 * The role carries NO authorization weight: the guard decides by RBAC roles → policies and never reads this
 * field. It survives as a display label and as a product-curation axis. `viewer` is invitable
 * (`INVITABLE_ROLES`), so leaving it out used to make an invited viewer fall through every `Record<UserRole,
 * …>` lookup.
 */
export const UserRole = {
  ADMIN: "admin",
  OPERATOR: "operator",
  SUPPORT: "support",
  DEVELOPER: "developer",
  USER: "user",
  VIEWER: "viewer",
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  SUSPENDED: "suspended",
  PENDING_VERIFICATION: "pending_verification",
} as const;

export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

/** The step-up methods coloca-nest accepts: the authenticator app (TOTP) or the security PIN. */
export type StepUpMethod = "totp" | "pin";

export interface UserEntity {
  id: string;
  full_name: string;
  email: string;
  password: string;
  email_verified_at?: Date;
  user_type: UserType;
  organization?: OrganizationEntity;
  organization_id?: string;
  role: UserRole;
  scopes: Permission[];
  status: UserStatus;
  last_login_at?: Date;
  profile_image?: string;
  country?: string;
  phone_number?: string;
  preferred_language?: string;
  timezone?: string;
  two_factor_enabled: boolean;
  two_factor_secret?: string;
  /**
   * The one method step-up actions (`x-two-factor-code`) accept, decided by coloca-nest: the authenticator
   * when enrolled, otherwise the security PIN of an organization member, otherwise none. Absent on a backend
   * that predates the PIN — read it through `stepUpMethodOf`, never directly.
   */
  step_up_method?: StepUpMethod | null;
  security_pin_enabled?: boolean;
  /**
   * ISO instant a temporary step-up lock ends (5 wrong codes in a row, with the authenticator or the PIN);
   * null when none is running.
   */
  step_up_locked_until?: string | null;
  /** 10 wrong PINs in a row: only a new PIN, set with the password, unlocks it — and every session was closed. */
  security_pin_reset_required?: boolean;
  /**
   * The email backup is on: a step-up challenge can send a one-time code to the account's email instead of
   * asking for the PIN or the authenticator code. On by default with a PIN, opt-in with the authenticator.
   */
  step_up_email_enabled?: boolean;
  /** The backup can be turned on: an organization member who already has a PIN or the authenticator. */
  step_up_email_available?: boolean;
  failed_login_attempts: number;
  locked_until?: Date;
  password_changed_at?: Date;
  must_change_password: boolean;
  reset_password_token?: string;
  reset_password_expires?: Date;
  created_at: Date;
  updated_at: Date;
  deleted_at?: Date;
}

/**
 * What `POST /auth/security-pin[/remove]` and `POST /auth/step-up/email-backup` answer with, and the part of
 * the user they change.
 */
export type StepUpState = Pick<
  UserEntity,
  | "step_up_method"
  | "security_pin_enabled"
  | "step_up_locked_until"
  | "security_pin_reset_required"
  | "step_up_email_enabled"
  | "step_up_email_available"
>;
